import { useState, useCallback } from 'react'
import { channelsAPI } from '../services/api'
import { createLogger } from '../utils/logger'
import { trackChannelMessageRead } from '../utils/analytics'

const logger = createLogger('ChannelMessages')

/**
 * useChannelMessages - Manages message fetching, pagination, and state
 * 
 * Provides functions to fetch messages for a channel, load older messages
 * with cursor-based pagination, and mark channels as read.
 * 
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @returns {Object} - { messages, setMessages, fetchMessages, loadMoreMessages, markAsRead, hasMoreMessages, loading, loadingOlderMessages }
 * 
 * @atomic - <150 lines, single purpose
 * @idempotent - Same channel/cursor produces same messages
 */
export function useChannelMessages(isAuthenticated) {
  const [messages, setMessages] = useState([])
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)

  const fetchMessages = useCallback(async (channelId, options = {}) => {
    if (!channelId || !isAuthenticated) return
    
    setLoading(true)
    try {
      const params = {
        limit: options.limit || 50,
        ...(options.since && { since: options.since })
      }
      
      const response = await channelsAPI.getChannelMessages(channelId, params)
      const newMessages = response.data.messages || []
      
      if (options.append) {
        setMessages(prev => [...prev, ...newMessages])
      } else if (options.replace) {
        // Replace all messages (used when switching channels)
        setMessages(newMessages)
      } else {
        // Merge with existing messages to preserve WebSocket messages that arrived during fetch
        setMessages(prev => {
          // If no previous messages, just use new ones
          if (prev.length === 0) return newMessages
          
          // Create a map of existing message IDs
          const existingIds = new Set(prev.map(m => m.id))
          
          // Add only messages from database that aren't already in state
          const uniqueNewMessages = newMessages.filter(m => !existingIds.has(m.id))
          
          // Merge and sort by createdAt (oldest first)
          return [...prev, ...uniqueNewMessages].sort((a, b) => 
            new Date(a.createdAt) - new Date(b.createdAt)
          )
        })
      }
      
      // Check if there might be more messages
      setHasMoreMessages(newMessages.length >= (options.limit || 50))
      
      // Track message read analytics
      if (newMessages.length > 0) {
        trackChannelMessageRead(channelId, newMessages.length)
      }
      
      return newMessages
    } catch (error) {
      logger.error('Failed to fetch messages', error, { channelId })
      throw error
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const loadMoreMessages = useCallback(async (channelId) => {
    if (!channelId || !isAuthenticated || messages.length === 0 || loadingOlderMessages) {
      return
    }
    
    setLoadingOlderMessages(true)
    try {
      // Use the oldest message as cursor
      const oldestMessage = messages[0]
      const response = await channelsAPI.getChannelMessagesPaginated(channelId, {
        limit: 50,
        before: oldestMessage.createdAt,
        before_id: oldestMessage.id
      })
      
      const olderMessages = response.data.messages || []
      const hasMore = response.data.hasMore || false
      
      // Prepend older messages to the list
      setMessages(prev => [...olderMessages, ...prev])
      setHasMoreMessages(hasMore)
      
      logger.info('Loaded older messages', { 
        channelId,
        count: olderMessages.length, 
        hasMore 
      })
      
      return olderMessages
    } catch (error) {
      logger.error('Failed to load older messages', error, { channelId })
      throw error
    } finally {
      setLoadingOlderMessages(false)
    }
  }, [isAuthenticated, messages, loadingOlderMessages])

  const markAsRead = useCallback(async (channelId) => {
    if (!channelId || !isAuthenticated) return
    
    try {
      await channelsAPI.markChannelAsRead(channelId)
    } catch (error) {
      logger.error('Failed to mark channel as read', error, { channelId })
      // Don't throw - this is a non-critical operation
    }
  }, [isAuthenticated])

  return {
    messages,
    setMessages,
    fetchMessages,
    loadMoreMessages,
    markAsRead,
    hasMoreMessages,
    loading,
    loadingOlderMessages
  }
}
